// Beginner view category groupings that map to Prompt Builder framework
// This is presentation-only - no scoring changes

import { DIMENSIONS, DimensionKey, ParsedDimension, IMPROVEMENT_TEMPLATES } from './rubricParser';

export interface BeginnerCategory {
  key: string;
  label: string;
  max: number;
  dimensions: DimensionKey[];
}

export const BEGINNER_CATEGORIES: BeginnerCategory[] = [
  {
    key: 'ai_persona',
    label: 'AI Persona',
    max: 6,
    dimensions: ['role_assignment'],
  },
  {
    key: 'context_goal',
    label: 'Context & Goal',
    max: 52,
    dimensions: ['outcome_clarity', 'context_background', 'constraints_scope', 'quality_bar'],
  },
  {
    key: 'task_sources',
    label: 'Task & Sources',
    max: 32,
    dimensions: ['inputs_sources', 'method_process', 'risk_controls', 'iteration_hygiene'],
  },
  {
    key: 'output_format',
    label: 'Output Format',
    max: 10,
    dimensions: ['output_format'],
  },
];

// Static "doing well" text per dimension (used for category roll-ups)
export const DOING_WELL_TEMPLATES: Record<DimensionKey, string> = {
  outcome_clarity: 'You clearly specify what you want the AI to produce.',
  role_assignment: 'You assign relevant expert roles to guide AI responses.',
  context_background: 'You provide helpful background information the AI needs.',
  constraints_scope: 'You set clear boundaries on what to include or exclude.',
  inputs_sources: 'You specify data sources and handling instructions.',
  output_format: 'You define structured output requirements.',
  quality_bar: 'You establish clear success criteria.',
  method_process: 'You guide the AI on approach and methodology.',
  risk_controls: 'You ask AI to flag uncertainty and assumptions.',
  iteration_hygiene: 'You plan for drafts and refinements.',
};

export interface CategoryScore {
  key: string;
  label: string;
  max: number;
  score: number;
  percentOfMax: number;
  doingWell: string;
  improve: string;
  dimensions: DimensionKey[];
}

// Aggregate dimension scores into beginner categories
export function aggregateDimensionsToCategories(
  dimensionScores: Record<string, number>
): CategoryScore[] {
  return BEGINNER_CATEGORIES.map(category => {
    // Sum scores for all dimensions in this category
    const score = category.dimensions.reduce((sum, dimKey) => {
      return sum + (dimensionScores[dimKey] ?? 0);
    }, 0);

    const percentOfMax = category.max > 0 ? score / category.max : 0;

    // Find best and worst dimensions for feedback
    const dimScoresWithPercent = category.dimensions.map(dimKey => {
      const dimDef = DIMENSIONS.find(d => d.key === dimKey);
      const dimScore = dimensionScores[dimKey] ?? 0;
      const dimMax = dimDef?.max ?? 1;
      return {
        key: dimKey,
        score: dimScore,
        max: dimMax,
        percent: dimMax > 0 ? dimScore / dimMax : 0,
      };
    });

    // Sort by percentage to find best and worst
    const sorted = [...dimScoresWithPercent].sort((a, b) => b.percent - a.percent);
    const best = sorted[0];
    const worst = sorted[sorted.length - 1];

    // Get feedback text
    const doingWell = DOING_WELL_TEMPLATES[best.key];
    const improve = worst.percent >= 0.9 
      ? 'Keep doing this.' 
      : IMPROVEMENT_TEMPLATES[worst.key];

    return {
      key: category.key,
      label: category.label,
      max: category.max,
      score,
      percentOfMax,
      doingWell,
      improve,
      dimensions: category.dimensions,
    };
  });
}

// Aggregate ParsedDimension array into categories (for ScoreBreakdown)
export function aggregateParsedDimensionsToCategories(
  dimensions: ParsedDimension[]
): CategoryScore[] {
  // Build a lookup map
  const dimLookup = new Map<DimensionKey, ParsedDimension>();
  dimensions.forEach(d => dimLookup.set(d.key, d));

  return BEGINNER_CATEGORIES.map(category => {
    // Sum scores for all dimensions in this category
    let score = 0;
    const dimDetails: Array<{ key: DimensionKey; score: number; max: number; percent: number; whatWorked: string; improvement: string }> = [];

    category.dimensions.forEach(dimKey => {
      const dim = dimLookup.get(dimKey);
      const dimDef = DIMENSIONS.find(d => d.key === dimKey);
      const dimScore = dim?.score ?? 0;
      const dimMax = dimDef?.max ?? 1;
      score += dimScore;
      dimDetails.push({
        key: dimKey,
        score: dimScore,
        max: dimMax,
        percent: dimMax > 0 ? dimScore / dimMax : 0,
        whatWorked: dim?.whatWorked ?? DOING_WELL_TEMPLATES[dimKey],
        improvement: dim?.improvement ?? IMPROVEMENT_TEMPLATES[dimKey],
      });
    });

    const percentOfMax = category.max > 0 ? score / category.max : 0;

    // Sort by percentage to find best and worst
    const sorted = [...dimDetails].sort((a, b) => b.percent - a.percent);
    const best = sorted[0];
    const worst = sorted[sorted.length - 1];

    // Use actual parsed feedback when available
    const doingWell = best.whatWorked;
    const improve = worst.percent >= 0.9 ? 'Keep doing this.' : worst.improvement;

    return {
      key: category.key,
      label: category.label,
      max: category.max,
      score,
      percentOfMax,
      doingWell,
      improve,
      dimensions: category.dimensions,
    };
  });
}

// Get dimension label from key
export function getDimensionLabel(key: DimensionKey): string {
  const dim = DIMENSIONS.find(d => d.key === key);
  return dim?.label ?? key;
}

// Get dimension max from key
export function getDimensionMax(key: DimensionKey): number {
  const dim = DIMENSIONS.find(d => d.key === key);
  return dim?.max ?? 0;
}
